import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useConfirm } from "@/shared/hooks/use-confirm";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/shared/ui/sheet";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import { Card, CardContent } from "@/shared/ui/card";
import { Copy, Loader2, MessageSquare, RefreshCw, Bell, X } from "lucide-react";
import { coachingService } from "../../api/coachingService";
import { useMutationWithToast } from "@/shared/hooks/useMutationWithToast";
import { toast } from "@/shared/hooks/use-toast";
import { fmtDatetime, taskStatusLabel } from "./helpers";

interface Props {
  taskId: number | null;
  onClose: () => void;
  /** 点员工行 → 打开对话记录 dialog(传 evaluationId)。 */
  onShowTranscript?: (evaluationId: number) => void;
  /** MANAGER+ 才显派发写按钮(重发/提醒/关闭);STAFF 只读看进度(#4 派单收紧,后端已 @PreAuthorize 403)。 */
  canManage: boolean;
}

export function TaskDetailDrawer({ taskId, onClose, onShowTranscript, canManage }: Props) {
  const { t } = useTranslation("coaching");
  const confirm = useConfirm();
  const {
    data: progress,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["task-progress", taskId],
    queryFn: () => coachingService.getTaskProgress(taskId as number),
    enabled: !!taskId,
    meta: { suppressGlobalErrorToast: true },
  });

  const redispatchMutation = useMutationWithToast({
    mutationFn: () => coachingService.redispatch(taskId as number, { scope: "undelivered" }),
    successToast: (n) => ({
      title: t("taskDetail.toastRedispatchTitle"),
      description: t("taskDetail.toastPeopleSuffix", { count: n }),
    }),
  });

  const remindMutation = useMutationWithToast({
    mutationFn: () => coachingService.remindIncomplete(taskId as number),
    successToast: (n) => ({
      title: t("taskDetail.toastRemindTitle"),
      description: t("taskDetail.toastPeopleSuffix", { count: n }),
    }),
  });

  const closeMutation = useMutationWithToast({
    mutationFn: () => coachingService.closeTask(taskId as number),
    successToast: t("taskDetail.toastClosed"),
  });

  return (
    <Sheet open={!!taskId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-3xl overflow-y-auto">
        {isError ? (
          // audit-0713 C1c:出错不再永久转圈
          <QueryErrorBanner isError className="mt-12" onRetry={() => void refetch()} />
        ) : isLoading || !progress ? (
          <div className="flex items-center justify-center h-40 mt-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                {progress.taskName}
                <Badge variant="outline" className="ml-2">
                  {taskStatusLabel(progress.status)}
                </Badge>
              </SheetTitle>
              <div className="text-xs text-muted-foreground">
                {t("taskDetail.scenePrefix")}
                {progress.scenarioName ?? "—"}
                {progress.deadline && (
                  <span>
                    {t("taskDetail.deadlinePrefix")}
                    {fmtDatetime(progress.deadline)}
                  </span>
                )}
              </div>
            </SheetHeader>

            <div className="grid grid-cols-3 gap-2 my-4">
              <Card>
                <CardContent className="p-3">
                  <div className="text-2xs text-muted-foreground">{t("taskDetail.assigned")}</div>
                  <div className="text-xl font-bold">{progress.assignedCount}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3">
                  <div className="text-2xs text-muted-foreground">{t("taskDetail.completed")}</div>
                  <div className="text-xl font-bold text-success">{progress.completedCount}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3">
                  <div className="text-2xs text-muted-foreground">{t("taskDetail.avgScore")}</div>
                  <div className="text-xl font-bold">
                    {progress.avgScore !== null ? progress.avgScore.toFixed(1) : "—"}
                  </div>
                </CardContent>
              </Card>
            </div>

            {(progress.topScore || progress.bottomScore) && (
              <div className="text-xs space-y-1 mb-4">
                {progress.topScore && (
                  <div>
                    {t("taskDetail.topScore")}
                    <span className="font-bold">{progress.topScore.score}</span>(
                    {progress.topScore.employeeName})
                  </div>
                )}
                {progress.bottomScore && (
                  <div>
                    {t("taskDetail.bottomScore")}
                    <span className="font-bold">{progress.bottomScore.score}</span>(
                    {progress.bottomScore.employeeName})
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2 mb-4 flex-wrap">
              {/* 派发写按钮(重发/提醒/关闭)= 管理动作,只给 MANAGER+;STAFF 打开详情只只读看进度。 */}
              {canManage && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => redispatchMutation.mutate()}
                    disabled={redispatchMutation.isPending}
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />
                    {t("taskDetail.redispatch")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => remindMutation.mutate()}
                    disabled={remindMutation.isPending}
                  >
                    <Bell className="h-4 w-4 mr-1" />
                    {t("taskDetail.remind")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      if (
                        await confirm({ title: t("taskDetail.confirmClose"), variant: "warning" })
                      )
                        closeMutation.mutate();
                    }}
                    disabled={closeMutation.isPending}
                  >
                    <X className="h-4 w-4 mr-1" />
                    {t("taskDetail.closeTask")}
                  </Button>
                </>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  const url = `${window.location.origin}/ai-peilian?task=${progress.taskId}`;
                  navigator.clipboard?.writeText(url).catch(() => {});
                  toast({ title: t("taskDetail.toastLinkCopied") });
                }}
              >
                <Copy className="h-4 w-4 mr-1" />
                {t("taskDetail.copyLink")}
              </Button>
            </div>
            {/* 1005:练习只在企业微信里进行,电脑浏览器打不开 —— 别让管理员把链接发给员工在浏览器里点 */}
            <p className="mb-4 rounded-md bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
              {t("taskDetail.practiceWhere")}
            </p>

            <h4 className="text-sm font-semibold mb-2">
              {t("taskDetail.memberHeading", {
                completed: progress.completedCount,
                pending: progress.pendingCount,
              })}
            </h4>
            <div className="border rounded-md divide-y max-h-96 overflow-y-auto">
              {(progress.members ?? []).map((m, i) => (
                <div
                  key={m.principalId}
                  className="flex items-center gap-3 p-3 fx-enter"
                  style={{ animationDelay: `${Math.min(i, 7) * 30}ms` }}
                >
                  {/* C4-3:FAILED=企微未送达(受派人零感知),标红警示区别于「已送达待练」⌛,防经理误判「只是没练」。 */}
                  <span
                    className={
                      m.completed
                        ? "text-success"
                        : m.dispatchStatus === "FAILED"
                          ? "text-destructive"
                          : "text-muted-foreground"
                    }
                    title={m.dispatchStatus === "FAILED" ? "企微未送达,需重发" : undefined}
                  >
                    {m.completed ? "✓" : m.dispatchStatus === "FAILED" ? "⚠" : "⌛"}
                  </span>
                  <div className="flex-1">
                    <span className="text-sm font-medium">{m.name ?? "—"}</span>
                    <span className="ml-2 text-xs text-muted-foreground">
                      {m.department ?? "—"}
                    </span>
                    {!m.completed && m.dispatchStatus === "FAILED" && (
                      <span className="ml-2 text-xs text-destructive">未送达</span>
                    )}
                  </div>
                  {m.completed && m.evaluationId && onShowTranscript && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs gap-1"
                      onClick={() => onShowTranscript(m.evaluationId as number)}
                    >
                      <MessageSquare className="h-3.5 w-3.5" />
                      {t("taskDetail.transcriptAction")}
                    </Button>
                  )}
                </div>
              ))}
              {progress.members.length === 0 && (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  {t("taskDetail.emptyMembers")}
                </div>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
